import { useState, useEffect, useRef } from "react";
import { API } from "@/api";
import { motion, AnimatePresence } from "framer-motion";
import { Radio, MapPin, Gift, Users } from "lucide-react";

const EVENT_ICONS = {
  truck_live: Radio,
  deal_claimed: Gift,
  spot_confirmed: MapPin,
  new_user: Users,
};

const EVENT_COLORS = {
  truck_live: "text-emerald-500",
  deal_claimed: "text-amber-500",
  spot_confirmed: "text-sky-500",
  new_user: "text-purple-500",
};

function timeAgo(ts) {
  const diff = (Date.now() - new Date(ts).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}

export default function LiveActivityFeed() {
  const [events, setEvents] = useState([]);
  const [connected, setConnected] = useState(false);
  const esRef = useRef(null);

  useEffect(() => {
    // Try SSE first, fallback to REST polling
    let es;
    try {
      es = new EventSource(`${API}/sse/activity-feed`, { withCredentials: true });
      esRef.current = es;

      es.onopen = () => setConnected(true);
      es.onmessage = (e) => {
        try {
          const evt = JSON.parse(e.data);
          setEvents(prev => {
            const next = [evt, ...prev];
            return next.slice(0, 8);
          });
        } catch { /* ignore malformed */ }
      };
      es.onerror = () => {
        setConnected(false);
        es.close();
        // Fallback: poll REST endpoint
        fetchRecent();
      };
    } catch {
      // SSE not supported — poll
      fetchRecent();
    }

    return () => {
      if (esRef.current) esRef.current.close();
    };
  }, []);

  const fetchRecent = async () => {
    try {
      const res = await fetch(`${API}/activity/recent`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setEvents((data.events || []).reverse().slice(0, 8));
      }
    } catch { /* ignore */ }
  };

  if (events.length === 0) return null;

  return (
    <div className="px-4 py-2" data-testid="live-activity-feed">
      <div className="flex items-center gap-1.5 mb-2">
        <span className={`w-1.5 h-1.5 rounded-full ${connected ? "bg-emerald-500 animate-pulse" : "bg-slate-300"}`} />
        <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Live Activity</span>
      </div>
      <div className="space-y-1 overflow-hidden max-h-[120px]">
        <AnimatePresence initial={false}>
          {events.slice(0, 3).map((evt, i) => {
            const Icon = EVENT_ICONS[evt.type] || Radio;
            const color = EVENT_COLORS[evt.type] || "text-slate-500";
            return (
              <motion.div
                key={evt.timestamp + i}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.25 }}
                className="flex items-center gap-2 py-1.5 px-2 rounded-lg bg-slate-50"
                data-testid={`activity-event-${i}`}
              >
                <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${color}`} />
                <span className="text-[11px] text-slate-600 truncate flex-1">{evt.message}</span>
                <span className="text-[9px] text-slate-400 flex-shrink-0">{timeAgo(evt.timestamp)}</span>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
}
